import React, { useCallback, useEffect, useState, useMemo } from 'react';
import parties from '../data/party';
import categoriesApi from '../data/categories';
import stockes from '../data/stockhistory';
import stockdata from '../data/stock';

interface Props {}

interface Party {
    id: number;
    date: string;
    name: string;
    number: string;
    contact_person_name: string;
    contact_person_number: string;
    categories: string[];
    deleteparty: number;
    total_amount?: number | string;
}

interface CategoryApiType {
    id: number | string;
    category_name: string;
}

interface Stock {
    id: number | string;
    name: string;
    category: string;
}

type StockCategoryInfo = {
    id: number | string;
    stockId: number | string;
    stockName: string;
    unit: string;
    totalAmount: string;
    indirectCharges: string;
    perUnitCharge: string;
    autoCalculated: boolean;
    category: string;
    error?: string;
};

type CategoryEntryMap = { [categoryName: string]: StockCategoryInfo[] };

type PartyStockDBType = {
    id: number | string;
    party_id: number | string;
    categories: string | (StockDBCategoryItem | DirectCategoryItem)[];
};

type StockDBCategoryItem = {
    id: number | string;
    investment_type: string;
    category: string;
    stock_name: string;
    unit: string;
    total_amount: string;
    indirect_charges: string;
    perUnitCharge: string;
};

type DirectCategoryItem = {
    investment_type: string;
    total_amount: string | number;
    date?: string;
    [key: string]: any;
};

let globalEntryId = 1;

async function fetchStocksAPI() {
    return [
        { id: 1, name: 'SBI', category: 'Equity' },
        { id: 2, name: 'HDFC', category: 'Equity' },
        { id: 3, name: 'RELIANCE', category: 'Equity' },
        { id: 4, name: 'NIFTY-FUT', category: 'FNO' },
        { id: 5, name: 'BANKNIFTY-OPT', category: 'FNO' },
        { id: 6, name: 'ICICI MF', category: 'Mutual Funds' },
        { id: 7, name: 'SBI MF', category: 'Mutual Funds' },
        { id: 8, name: 'NIFTY ETF', category: 'ETF' },
        { id: 9, name: 'GOLD ETF', category: 'ETF' },
        { id: 10, name: 'ETF Option A', category: 'ETFO' },
        { id: 11, name: 'ETF Option B', category: 'ETFO' },
    ];
}

function emptyStockCategoryInfo(category = '', stockId: number | string = '', stockName = ''): StockCategoryInfo {
    const id = globalEntryId++;
    return {
        id,
        stockId,
        stockName,
        category,
        unit: '',
        totalAmount: '',
        indirectCharges: '',
        perUnitCharge: '',
        autoCalculated: true,
        error: undefined
    };
}

function getAvailableUnit(
    categoryEntries: CategoryEntryMap,
    category: string,
    stockId: number | string
): number {
    if (!category || !stockId) return 0;
    const entries = (categoryEntries[category] || []).filter(
        entry => entry.stockId == stockId
    );
    let total = 0;
    for (const entry of entries) {
        const n = parseFloat(entry.unit);
        if (!isNaN(n)) total += n;
    }
    return total;
}

// Returns (sum of): category allocations (total_amount+indirect_charges), PLUS direct payments (total_amount where investment_type==='direct') for given party
function calculateTotalDeductedIncludingDirect(stockDbRows: PartyStockDBType[], partyId: string | number): number {
    let totalDeducted = 0;
    stockDbRows.forEach(row => {
        if (String(row.party_id) !== String(partyId)) return;
        let catsArr: (StockDBCategoryItem | DirectCategoryItem)[] = [];
        if (typeof row.categories === 'string') {
            try {
                const parsed = JSON.parse(row.categories);
                catsArr = Array.isArray(parsed) ? parsed : [];
            } catch {
                catsArr = [];
            }
        } else if (Array.isArray(row.categories)) {
            catsArr = row.categories as (StockDBCategoryItem | DirectCategoryItem)[];
        }
        for (const item of catsArr) {
            if (
                typeof item === "object" &&
                (item as any).investment_type === "direct" &&
                (item as any).total_amount !== undefined
            ) {
                // Sum up direct investment_type total_amount for deduction
                let amt = parseFloat(String((item as any).total_amount) ?? "0");
                if (!isNaN(amt)) totalDeducted += amt;
            } else if (
                typeof item === "object" &&
                (item as any).category
            ) {
                let amt = parseFloat((item as StockDBCategoryItem).total_amount ?? "0");
                let indirect = parseFloat((item as StockDBCategoryItem).indirect_charges ?? "0");
                if (!isNaN(amt)) totalDeducted += amt;
                if (!isNaN(indirect)) totalDeducted += indirect;
            }
        }
    });
    return totalDeducted;
}

// Only returns sum of (total_amount+indirect_charges) for category allocations, NOT direct payments
function calculateTotalDeductedFromStockTable(stockDbRows: PartyStockDBType[], partyId: string | number): number {
    let totalDeducted = 0;
    stockDbRows.forEach(row => {
        if (String(row.party_id) !== String(partyId)) return;
        let catsArr: StockDBCategoryItem[] = [];
        if (typeof row.categories === 'string') {
            try {
                const parsed = JSON.parse(row.categories);
                catsArr = Array.isArray(parsed) ? parsed : [];
            } catch {
                catsArr = [];
            }
        } else if (Array.isArray(row.categories)) {
            catsArr = row.categories as StockDBCategoryItem[];
        }
        for (const item of catsArr) {
            if (typeof item === "object" && 'category' in item) {
                let amt = parseFloat(item.total_amount ?? "0");
                let indirect = parseFloat(item.indirect_charges ?? "0");
                if (!isNaN(amt)) totalDeducted += amt;
                if (!isNaN(indirect)) totalDeducted += indirect;
            }
        }
    });
    return totalDeducted;
}

// ---- Sell Stock Modal logic ----

type SellStockFormState = {
    partyId: string;
    category: string;
    stockId: string | number;
    stockName: string;
    sellUnits: string;
    sellAmount: string;
    indirectCharges: string;

    date: string;
};

function getStockUnitAndTotalAmount(stocksDbRows: PartyStockDBType[], partyId: string, category: string, stockId: string | number, stockName: string): {totalUnits: number, totalAmount: number} {
    // Go through DB rows for the party, find sum units & amount for this stock in the selected category (minus sold/withdrawals is not handled in this simplified view)
    let totalUnits = 0;
    let totalAmount = 0;
    for (const row of stocksDbRows) {
        if (String(row.party_id) !== String(partyId)) continue;
        let catsArr: StockDBCategoryItem[] = [];
        if (typeof row.categories === 'string') {
            try {
                const parsed = JSON.parse(row.categories);
                catsArr = Array.isArray(parsed) ? parsed as StockDBCategoryItem[] : [];
            } catch { catsArr = [] }
        } else if (Array.isArray(row.categories)) {
            catsArr = row.categories as StockDBCategoryItem[];
        }
        for (const item of catsArr) {
            if (
                item.category === category &&
                (String(item.id) === String(stockId) || String(item.stock_name) === String(stockName))
            ) {
                const u = parseFloat(item.unit);
                const a = parseFloat(item.total_amount);
                if (!isNaN(u)) totalUnits += u;
                if (!isNaN(a)) totalAmount += a;
            }
        }
    }
    return {totalUnits, totalAmount};
}

function getAvailableDBStocksForParty(stocksDbRows: PartyStockDBType[], partyId: string) {
    // creates a structure: { [category]: StockDBCategoryItem[] }
    let out: { [category: string]: StockDBCategoryItem[] } = {};
    for (const row of stocksDbRows) {
        if (String(row.party_id) !== String(partyId)) continue;
        let catsArr: StockDBCategoryItem[] = [];
        if (typeof row.categories === 'string') {
            try {
                const parsed = JSON.parse(row.categories);
                catsArr = Array.isArray(parsed) ? parsed as StockDBCategoryItem[] : [];
            } catch { catsArr = [] }
        } else if (Array.isArray(row.categories)) {
            catsArr = row.categories as StockDBCategoryItem[];
        }
        for (const item of catsArr) {
            if (!out[item.category]) out[item.category] = [];
            out[item.category].push(item);
        }
    }
    return out;
}

function Addstock(props: Props) {
    // --- existing state and hooks ---
    const [data, setData] = useState({
        partyId: '',
        date: '',
    });

    const [investmentType, setInvestmentType] = useState<'direct' | 'structure'>('structure');
    const [directPaymentAmount, setDirectPaymentAmount] = useState('');
    const [hasDirectPending, setHasDirectPending] = useState(false);

    const [categoryEntries, setCategoryEntries] = useState<CategoryEntryMap>({});
    const [partiesList, setPartiesList] = useState<Party[]>([]);
    const [allStocks, setAllStocks] = useState<Stock[]>([]);
    const [categoriesFromApi, setCategoriesFromApi] = useState<CategoryApiType[]>([]);
    const [possibleStocksByCategory, setPossibleStocksByCategory] = useState<{ [cat: string]: Stock[] }>({});
    const [possibleCategories, setPossibleCategories] = useState<CategoryApiType[]>([]);
    const [stocksDb, setStocksDb] = useState<PartyStockDBType[]>([]);
    const [stocksDataDb, setStocksDataDb] = useState<PartyStockDBType[]>([]);
    const [withdrawCategoriesDb, setWithdrawCategoriesDb] = useState<string[]>([]);
    const [withdrawStocksDb, setWithdrawStocksDb] = useState<StockDBCategoryItem[]>([]);
    const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);

    const [withdrawalInvestmentType, setWithdrawalInvestmentType] = useState<'direct' | 'structure'>('structure');
    const [withdrawalDirectPayment, setWithdrawalDirectPayment] = useState('');

    const [withdrawal, setWithdrawal] = useState<{
        partyId: string;
        category: string;
        stockId: number | string;
        stockName: string;
        sale: string;
        date: string;
    }>({
        partyId: '',
        category: '',
        stockId: '',
        stockName: '',
        sale: '',
        date: '',
    });
    const [withdrawalLoading, setWithdrawalLoading] = useState(false);

    const [selectedPartyTotalAmount, setSelectedPartyTotalAmount] = useState<number | null>(null);
    const [deductedAvailable, setDeductedAvailable] = useState<number | null>(null);

    const [entryErrors, setEntryErrors] = useState<{ [cat: string]: { [idx: number]: string } }>({});

    const [directToStructureMeta, setDirectToStructureMeta] = useState<{directPaymentAmount: string, investmentType: "direct", metaDate: string} | null>(null);

    // ----- MODAL for Sell Stock ----
    const [showSellStockModal, setShowSellStockModal] = useState(false);
    const [sellStockLoading, setSellStockLoading] = useState(false);
    const [sellStockForm, setSellStockForm] = useState<SellStockFormState>({
        partyId: '',
        category: '',
        stockId: '',
        stockName: '',
        sellUnits: '',
        sellAmount:'',
        indirectCharges:"",
        date: '',
    });
    const [sellStockFormAmount, setSellStockFormAmount] = useState<number>(0);

    // Fill categories/stocks that are available to the selected party
    const sellAvailableDBStocks = useMemo(() => getAvailableDBStocksForParty(stocksDataDb, sellStockForm.partyId), [stocksDataDb, sellStockForm.partyId]);
    const sellPartyCategories = useMemo(
        () => Object.keys(sellAvailableDBStocks), [sellAvailableDBStocks]
    );

    // Stocks in selected category for sellStock modal
    const sellCategoryStocks = useMemo(() => {
        if (!sellStockForm.category) return [];
        return (sellAvailableDBStocks[sellStockForm.category] || []);
    }, [sellStockForm.category, sellAvailableDBStocks]);


    // Get currently available units and totalAmount for computation
    const sellStockAvailableDetails = useMemo(() => {
        if (!sellStockForm.partyId || !sellStockForm.category || !sellStockForm.stockId) {
            return { unit: 0, amount: 0, current_price: 0 };
        }
        // Find matching DB item for party/category/stockId
        let foundItem = sellCategoryStocks.find(
            s =>
                String(s.id) === String(sellStockForm.stockId)
                || String(s.stock_name) === String(sellStockForm.stockName)
        );
        if (!foundItem) return { unit: 0, amount: 0, current_price: 0 };

        let nUnit = parseFloat(foundItem.unit || '0');
        let nPrice = parseFloat(foundItem.total_amount || '0'); // treating total_amount as current_price here

        return {
            unit: !isNaN(nUnit) ? nUnit : 0,
            amount: !isNaN(nPrice) ? nPrice : 0, // Keeping legacy "amount" for downstream usage
            current_price: !isNaN(nPrice) ? nPrice : 0,
            perUnit: !isNaN(nUnit) && nUnit > 0 && !isNaN(nPrice) ? nPrice / nUnit : 0,
        };
    }, [sellStockForm.partyId, sellStockForm.category, sellStockForm.stockId, sellStockForm.stockName, sellCategoryStocks]);

    useEffect(() => {
        // Compute expected sale amount as soon as sellUnits change
        let unitsNum = parseFloat(sellStockForm.sellUnits || '0');
        let {unit, amount} = sellStockAvailableDetails;
        let perUnit = unit > 0 ? amount / unit : 0;
        let calc = !isNaN(unitsNum) ? perUnit * unitsNum : 0;
        setSellStockFormAmount(calc);
    }, [sellStockForm, sellStockAvailableDetails]);

    const handleSellStockFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        let patch: Partial<SellStockFormState> = { [name]: value };
        // Reset following fields on changes:
        if (name === "partyId") {
            patch.category = '';
            patch.stockId = '';
            patch.stockName = '';
            patch.sellUnits = '';
            patch.date = '';
        }
        if (name === "category") {
            patch.stockId = '';
            patch.stockName = '';
            patch.sellUnits = '';
        }
        if (name === "stockId") {
            // Find stock name for this stockId
            const found = sellCategoryStocks.find(s => String(s.id) === value);
            patch.stockName = found ? found.stock_name : '';
        }
        setSellStockForm(s => ({ ...s, ...patch }));
    };

    // Modal submit logic
    const handleSellStockSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!sellStockForm.partyId) {
            alert('Select party');
            return;
        }
        if (!sellStockForm.category) {
            alert('Select category');
            return;
        }
        if (!sellStockForm.stockId) {
            alert('Select stock');
            return;
        }
        let sellUnitsNumber = parseFloat(sellStockForm.sellUnits || '0');
        if (!sellStockForm.sellUnits || isNaN(sellUnitsNumber) || sellUnitsNumber <= 0) {
            alert('Please enter valid units to sell');
            return;
        }
        if (sellUnitsNumber > sellStockAvailableDetails.unit) {
            alert(
                `Not enough units available to sell! Available: ${sellStockAvailableDetails.unit}, Requested: ${sellUnitsNumber}`
            );
            return;
        }
        if (!sellStockForm.date) {
            alert("Select date for sell");
            return;
        }

        // Calculate amount
        const expectedAmount = sellStockFormAmount;

        // Compose API request (implementation specific!)
        setSellStockLoading(true);
        try {
            // This should be replaced with your real API endpoint.
            const payload = {
                party_id: sellStockForm.partyId ?? null,
                category: sellStockForm.category ?? null,
                stock_id: sellStockForm.stockId ?? null,
                stock_name: sellStockForm.stockName ?? null,
                sell_units: sellUnitsNumber ?? null,
                sell_amount: expectedAmount ?? null,
                indirect_charges: sellStockForm.indirectCharges ?? null,
                date: sellStockForm.date ?? null
            };
            const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/sellstocks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await res.json();
            if (!res.ok) throw new Error(result?.message || 'Failed to sell stock');
            alert('Stock sell recorded successfully!');
            setShowSellStockModal(false);
            setSellStockForm({
                partyId: '',
                category: '',
                stockId: '',
                stockName: '',
                sellUnits: '',
                sellAmount:"",
                indirectCharges:"",
                date: '',
            });
            // Refresh the page or fetch state as necessary
            window.location.reload();
        } catch (err: any) {
            alert('Error: ' + (err?.message ?? 'Something went wrong'));
        } finally {
            setSellStockLoading(false);
        }
    };

    // End --- MODAL for sell stock ----

    const fetchParties = useCallback(async () => {
        const res = await parties();
        const cleaned = Array.isArray(res)
            ? res.map((item: any) => ({
                ...item,
                categories:
                    typeof item.categories === 'string'
                        ? (() => {
                            try {
                                const parsed = JSON.parse(item.categories);
                                return Array.isArray(parsed) ? parsed : [item.categories];
                            } catch {
                                return [item.categories];
                            }
                        })()
                        : Array.isArray(item.categories)
                            ? item.categories
                            : [],
                total_amount: item.total_amount !== undefined ? Number(item.total_amount) : undefined,
            }))
            : [];
        setPartiesList(cleaned);
    }, []);

    const fetchCategories = useCallback(async () => {
        const res = await categoriesApi();
        if (Array.isArray(res)) {
            setCategoriesFromApi(res);
            setPossibleCategories(res);
        }
    }, []);

    const fetchAllStocksForCategories = useCallback(async () => {
        const stocks: Stock[] = await fetchStocksAPI();
        setAllStocks(stocks);

        const byCategory: { [cat: string]: Stock[] } = {};
        stocks.forEach(stock => {
            if (!byCategory[stock.category]) byCategory[stock.category] = [];
            byCategory[stock.category].push(stock);
        });
        setPossibleStocksByCategory(byCategory);
    }, []);

    const fetchStocksDb = useCallback(async () => {
        try {
            const res = await stockes();
            setStocksDb(Array.isArray(res) ? res : []);
        } catch (e) {
            setStocksDb([]);
        }
    }, []);

    const fetchStocksDataDb = useCallback(async () => {
        try {
            const res = await stockdata();
            setStocksDataDb(Array.isArray(res) ? res : []);
        } catch (e) {
            setStocksDataDb([]);
        }
    }, []);

    useEffect(() => {
        fetchParties();
        fetchCategories();
        fetchAllStocksForCategories();
        fetchStocksDb();
        fetchStocksDataDb();
    }, [fetchParties, fetchCategories, fetchAllStocksForCategories, fetchStocksDb, fetchStocksDataDb]);

    useEffect(() => {
        if (data.partyId && partiesList.length > 0) {
            const party = partiesList.find(p => String(p.id) === String(data.partyId));
            if (party && party.total_amount !== undefined && party.total_amount !== null && !isNaN(Number(party.total_amount))) {
                setSelectedPartyTotalAmount(Number(party.total_amount));
            } else {
                setSelectedPartyTotalAmount(null);
            }
        } else {
            setSelectedPartyTotalAmount(null);
        }
    }, [data.partyId, partiesList]);

    // !! The available amount (after deduction) considers both direct payments and structure/indirect charges
    useEffect(() => {
        if (!selectedPartyTotalAmount) {
            setDeductedAvailable(null);
            return;
        }

        if (!data.partyId || !stocksDataDb.length) {
            setDeductedAvailable(selectedPartyTotalAmount);
            return;
        }

        // 1. Get total deducted amount up to now (from stockdata) including direct amounts
        const dbDeducted = calculateTotalDeductedIncludingDirect(stocksDataDb, data.partyId);

        if (investmentType === "direct") {
            let deducted = selectedPartyTotalAmount - dbDeducted;
            if (directPaymentAmount && !isNaN(Number(directPaymentAmount))) {
                deducted = deducted - Number(directPaymentAmount);
            }
            setDeductedAvailable(deducted);
        } else if (investmentType === "structure") {
            // sum of new entries (totalAmount + indirectCharges)
            let uiDeducted = 0;
            Object.entries(categoryEntries).forEach(([category, ents]) => {
                ents.forEach(ent => {
                    let amt = parseFloat(ent.totalAmount || "0");
                    if (isNaN(amt)) amt = 0;
                    let indirect = parseFloat(ent.indirectCharges || "0");
                    if (isNaN(indirect)) indirect = 0;
                    uiDeducted += amt + indirect;
                });
            });
            // support direct-to-structure transition: add pending direct (if any)
            let pendingDirect = directToStructureMeta ? Number(directToStructureMeta.directPaymentAmount) || 0 : 0;
            setDeductedAvailable(selectedPartyTotalAmount - dbDeducted - uiDeducted - pendingDirect);
        }
    }, [selectedPartyTotalAmount, investmentType, directPaymentAmount, categoryEntries, data.partyId, stocksDataDb, directToStructureMeta]);

    useEffect(() => {
        if (investmentType === "direct") setHasDirectPending(false);
        if (investmentType === "structure" && directToStructureMeta) setHasDirectPending(true);
    }, [investmentType, directToStructureMeta]);

    // LIVE validation for structure entries - disables totalAmount/indirectCharges input if over-allocating
    useEffect(() => {
        if (investmentType !== "structure" || !data.partyId || !selectedPartyTotalAmount || !stocksDataDb.length) {
            setEntryErrors({});
            return;
        }
        const alreadyDeducted = calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId); // only category allocations
        let accum = directToStructureMeta ? Number(directToStructureMeta.directPaymentAmount) || 0 : 0;
        let errors: { [cat: string]: { [idx: number]: string } } = {};
        Object.entries(categoryEntries).forEach(([category, entries]) => {
            let errs: { [idx: number]: string } = {};
            entries.forEach((entry, idx) => {
                let amt = Number(entry.totalAmount) || 0;
                let indirect = Number(entry.indirectCharges) || 0;
                accum += (amt + indirect);
                if (alreadyDeducted + accum > selectedPartyTotalAmount) {
                    errs[idx] = "Exceeds available amount!";
                }
            });
            if (Object.keys(errs).length) errors[category] = errs;
        });
        setEntryErrors(errors);
    }, [categoryEntries, stocksDataDb, data.partyId, selectedPartyTotalAmount, investmentType, directToStructureMeta]);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setData(prev => ({
            ...prev,
            [name]: value,
        }));
        if (name === "partyId") {
            setInvestmentType('structure');
            setCategoryEntries({});
            setDirectPaymentAmount('');
            setDirectToStructureMeta(null);
        }
        // For direct payment amount, ensure state update
        if (name === "directPaymentAmount") {
            setDirectPaymentAmount(value);
        }
    };

    const handleInvestmentTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value as 'direct' | 'structure';
        setInvestmentType(value);
        setDirectPaymentAmount('');
        setCategoryEntries({});
        setDirectToStructureMeta(null);
        setHasDirectPending(false);
    };

    const isChecked = (cat: CategoryApiType) => Object.prototype.hasOwnProperty.call(categoryEntries, cat.category_name);

    const handleCategory = (cat: CategoryApiType) => {
        const category = cat.category_name;
        setCategoryEntries(prev => {
            const copy = { ...prev };
            if (copy[category]) {
                delete copy[category];
            } else {
                copy[category] = [
                    emptyStockCategoryInfo(
                        category,
                        '',
                        ''
                    ),
                ];
            }
            return copy;
        });
    };

    const handleCategoryFieldChange = (
        cat: CategoryApiType,
        entryIdx: number,
        field: keyof StockCategoryInfo,
        value: string
    ) => {
        const category = cat.category_name;
        setCategoryEntries(prev => {
            const entries = prev[category] ? [...prev[category]] : [];
            const entry = entries[entryIdx] ? { ...entries[entryIdx] } : emptyStockCategoryInfo(category);

            let autoCalculated = entry.autoCalculated;
            let newPerUnitCharge = entry.perUnitCharge;

            let total = field === "totalAmount" ? value : entry.totalAmount || "0";
            let indirect = field === "indirectCharges" ? value : entry.indirectCharges || "0";
            let unit = field === "unit" ? value : entry.unit || "0";

            let newEntry = { ...entry };

            if (field === 'stockName') {
                newEntry.stockName = value;

                if (value && value.trim()) {
                    const stock = allStocks.find(
                        s => s.category === category && (
                            String(s.name).toLowerCase() === String(value).toLowerCase() ||
                            String(s.id) === String(value)
                        )
                    );
                    if (stock) {
                        newEntry.stockId = stock.id;
                    } else {
                        newEntry.stockId = value;
                    }
                } else {
                    newEntry.stockId = '';
                }
            } else if (field === 'stockId') {
                newEntry.stockId = value;
                if (value && value.trim()) {
                    const stock = allStocks.find(
                        s => s.category === category && (
                            String(s.id) === String(value) ||
                            String(s.name).toLowerCase() === String(value).toLowerCase()
                        )
                    );
                    newEntry.stockName = stock ? stock.name : value;
                } else {
                    newEntry.stockName = '';
                }
            }

            // Fix: Allow perUnitCharge zero for ETF/ETFO category, otherwise as before.
            if (field === "unit" || field === "totalAmount" || field === "indirectCharges") {
                const unitN = parseFloat(unit);
                const totalAmountN = parseFloat(total || "0");
                const indirectChargesN = parseFloat(indirect || "0");
                if (unitN > 0) {
                    newPerUnitCharge = ((totalAmountN + indirectChargesN) / unitN).toFixed(2);
                    autoCalculated = true;
                } else {
                    newPerUnitCharge = '';
                    autoCalculated = true;
                }
                newEntry[field] = value;
                newEntry.perUnitCharge = newPerUnitCharge;
                newEntry.autoCalculated = autoCalculated;
            } else if (field === "perUnitCharge") {
                autoCalculated = false;
                newPerUnitCharge = value;
                newEntry.perUnitCharge = value;
                newEntry.autoCalculated = false;
            } else if (field !== "stockId" && field !== "stockName") {
                (newEntry as any)[field] = value;
            }

            entries[entryIdx] = newEntry;
            return { ...prev, [category]: entries };
        });
    };

    const handleAddEntry = (cat: CategoryApiType) => {
        const category = cat.category_name;
        setCategoryEntries(prev => {
            const entries = prev[category] ? [...prev[category]] : [];
            return {
                ...prev,
                [category]: [
                    ...entries,
                    emptyStockCategoryInfo(
                        category,
                        '',
                        ''
                    ),
                ],
            };
        });
    };

    const handleRemoveEntry = (cat: CategoryApiType, entryIdx: number) => {
        const category = cat.category_name;
        setCategoryEntries(prev => {
            const entries = prev[category] ? [...prev[category]] : [];
            entries.splice(entryIdx, 1);
            const newEntries = entries.length === 0 ? undefined : entries;
            const copy: CategoryEntryMap = { ...prev };
            if (newEntries && newEntries.length > 0) {
                copy[category] = newEntries;
            } else {
                delete copy[category];
            }
            return copy;
        });
    };

    // ---- SUBMIT ----
    const handleSubmit = async (e?: React.FormEvent) => {
        debugger;
        if (e) e.preventDefault();
        if (!data.partyId) {
            alert('Please select a party');
            return;
        }
        let party: Party | undefined = partiesList.find(p => String(p.id) === String(data.partyId));
        let availableTotalAmount = (party && party.total_amount !== undefined && party.total_amount !== null && !isNaN(Number(party.total_amount))) ? Number(party.total_amount) : null;
        if (availableTotalAmount == null) {
            alert("Could not determine total amount available for the selected party.");
            return;
        }

        // Already-deducted: count structure (category total_amount + indirect_charges) AND direct investment_type entries
        const alreadyDeducted = calculateTotalDeductedIncludingDirect(stocksDataDb, data.partyId);

        // If direct payment selected and no direct-'pending', just submit as direct payment NOW (no categories needed)
        if (investmentType === 'direct' && !hasDirectPending) {
            debugger;
            if (!directPaymentAmount || isNaN(Number(directPaymentAmount)) || Number(directPaymentAmount) <= 0) {
                alert('Please enter a valid payment amount');
                return;
            }
            if (Number(directPaymentAmount) > (availableTotalAmount - alreadyDeducted)) {
                alert(`You don't have enough amount after deduction. Available: ${availableTotalAmount - alreadyDeducted}, required: ${directPaymentAmount}`);
                return;
            }
            const categories = [] as any[];
            const selectedCategories = Object.keys(categoryEntries);
            if (selectedCategories.length === 0) {
                alert('Please select at least one category');
                return;
            }
            let addedDeduct = 0;
            for (const category of selectedCategories) {
                const entries = categoryEntries[category];
                if (!Array.isArray(entries) || entries.length === 0) {
                    alert(`Please add at least one entry for category "${category}"`);
                    return;
                }
               
            }
    
            Object.entries(categoryEntries).forEach(([category, entries]) => {
                entries.forEach((catData) => {
                    categories.push({
                      investment_type: "direct",
                       total_amount: Number(directPaymentAmount),
                        date: data.date ?? null,
                        id: catData.id,
                        category: category,
                    });
                });
            });
            // Submit direct payment now
            let payload: any = {
                party_id: data.partyId,
                date: data.date ?? null,
                investment_type: "direct",
                direct_payment_amount: directPaymentAmount,
                categories
            };
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                const result = await res.json();
                if (!res.ok) throw new Error(result?.message || 'Failed to add stock');
                alert('Stock (Direct payment) added successfully!');
                setData({ partyId: '', date: '' });
                setCategoryEntries({});
                setDirectPaymentAmount('');
                setInvestmentType('structure');
                setDirectToStructureMeta(null);
                setHasDirectPending(false);
                globalEntryId = 1;
                // RELOAD PAGE after submit
                window.location.reload();
            } catch (err: any) {
                alert('Error: ' + (err?.message ?? 'Something went wrong'));
            }
            return;
        }

        if (investmentType === 'structure' && hasDirectPending && directToStructureMeta) {
            // Validate that total allocation matches direct amount
            const selectedCategories = Object.keys(categoryEntries);
            if (selectedCategories.length === 0) {
                alert('Please select at least one category');
                return;
            }
            let addedDeduct = 0;
            for (const category of selectedCategories) {
                const entries = categoryEntries[category];
                if (!Array.isArray(entries) || entries.length === 0) {
                    alert(`Please add at least one entry for category "${category}"`);
                    return;
                }
                for (const [idx, catInfo] of entries.entries()) {
                    if (!catInfo.stockName || !catInfo.stockName.trim()) {
                        alert(`Stock name required for entry ${idx + 1} of category "${category}"`);
                        return;
                    }
                    if (!catInfo.unit || isNaN(Number(catInfo.unit)) || Number(catInfo.unit) <= 0) {
                        alert(`Please enter a valid unit for entry ${idx + 1} of category "${category}"`);
                        return;
                    }
                    if (
                        !catInfo.totalAmount ||
                        isNaN(Number(catInfo.totalAmount)) ||
                        Number(catInfo.totalAmount) < 0
                    ) {
                        alert(`Please enter valid total amount for entry ${idx + 1} of category "${category}"`);
                        return;
                    }
                    if (
                        catInfo.indirectCharges !== "" &&
                        (isNaN(Number(catInfo.indirectCharges)) || Number(catInfo.indirectCharges) < 0)
                    ) {
                        alert(`Indirect Charges for entry ${idx + 1} of category "${category}" must be 0 or positive`);
                        return;
                    }
                    // Change: allow perUnitCharge zero for 'ETF' or 'ETFO'
                    if (
                        (
                            !catInfo.perUnitCharge ||
                            isNaN(Number(catInfo.perUnitCharge)) ||
                            (!['ETF', 'ETFO'].includes(category) && Number(catInfo.perUnitCharge) <= 0)
                        )
                    ) {
                        alert(`Please enter a valid per unit charge for entry ${idx + 1} of category "${category}"`);
                        return;
                    }
                    if (!catInfo.stockId || !String(catInfo.stockId).trim()) {
                        alert(`Stock ID cannot be blank for entry ${idx + 1} of category "${category}" (Stock name: "${catInfo.stockName}")`);
                        return;
                    }
                    let thisAmt = Number(catInfo.totalAmount) || 0;
                    let thisIndirect = Number(catInfo.indirectCharges) || 0;
                    addedDeduct += thisAmt + thisIndirect;
                }
            }
            // Allocation should equal directPaymentAmount (within 0.01 margin)
            const target = Number(directToStructureMeta.directPaymentAmount);
            if (Math.abs(addedDeduct - target) > 0.01) {
                alert(`Please allocate entries exactly matching direct payment amount (${target}). Allocated: ${addedDeduct}`);
                return;
            }
            let categories: any[] = [];
            Object.entries(categoryEntries).forEach(([category, entries]) => {
                entries.forEach((catData) => {
                    categories.push({
                        id: catData.id,
                        
                        stock_name: catData.stockName,
                        unit: catData.unit,
                        total_amount: catData.totalAmount,
                        indirect_charges: catData.indirectCharges ?? null,
                        perUnitCharge: catData.perUnitCharge,
                        category: category,
                        investment_type: "direct",
                        total_amount_category: catData.totalAmount
                    });
                });
            });
            let payload: any = {
                party_id: data.partyId,
                date: directToStructureMeta.metaDate ?? data.date ?? null,
                investment_type: "direct",
                direct_payment_amount: directToStructureMeta.directPaymentAmount,
                categories: categories,
            };
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                const result = await res.json();
                if (!res.ok) throw new Error(result?.message || 'Failed to add stock');
                alert('Stock (Direct with Categories) added successfully!');
                setData({ partyId: '', date: '' });
                setCategoryEntries({});
                setDirectPaymentAmount('');
                setInvestmentType('structure');
                setDirectToStructureMeta(null);
                setHasDirectPending(false);
                globalEntryId = 1;
                // RELOAD PAGE after submit
                window.location.reload();
            } catch (err: any) {
                alert('Error: ' + (err?.message ?? 'Something went wrong'));
            }
            return;
        }

        if (investmentType === 'structure') {
            const selectedCategories = Object.keys(categoryEntries);
            if (selectedCategories.length === 0) {
                alert('Please select at least one category');
                return;
            }
            let addedDeduct = 0;

            for (const category of selectedCategories) {
                const entries = categoryEntries[category];
                if (!Array.isArray(entries) || entries.length === 0) {
                    alert(`Please add at least one entry for category "${category}"`);
                    return;
                }
                for (const [idx, catInfo] of entries.entries()) {
                    if (!catInfo.stockName || !catInfo.stockName.trim()) {
                        alert(`Stock name required for entry ${idx + 1} of category "${category}"`);
                        return;
                    }
                    if (!catInfo.unit || isNaN(Number(catInfo.unit)) || Number(catInfo.unit) <= 0) {
                        alert(`Please enter a valid unit for entry ${idx + 1} of category "${category}"`);
                        return;
                    }
                    if (
                        !catInfo.totalAmount ||
                        isNaN(Number(catInfo.totalAmount)) ||
                        Number(catInfo.totalAmount) < 0
                    ) {
                        alert(`Please enter valid total amount for entry ${idx + 1} of category "${category}"`);
                        return;
                    }
                    if (
                        catInfo.indirectCharges !== "" &&
                        (isNaN(Number(catInfo.indirectCharges)) || Number(catInfo.indirectCharges) < 0)
                    ) {
                        alert(`Indirect Charges for entry ${idx + 1} of category "${category}" must be 0 or positive`);
                        return;
                    }
                    // Change: allow perUnitCharge zero for 'ETF' or 'ETFO'
                    if (
                        (
                            !catInfo.perUnitCharge ||
                            isNaN(Number(catInfo.perUnitCharge)) ||
                            (!['ETF', 'ETFO'].includes(category) && Number(catInfo.perUnitCharge) <= 0)
                        )
                    ) {
                        alert(`Please enter a valid per unit charge for entry ${idx + 1} of category "${category}"`);
                        return;
                    }
                    if (!catInfo.stockId || !String(catInfo.stockId).trim()) {
                        alert(`Stock ID cannot be blank for entry ${idx + 1} of category "${category}" (Stock name: "${catInfo.stockName}")`);
                        return;
                    }
                    let thisAmt = Number(catInfo.totalAmount) || 0;
                    let thisIndirect = Number(catInfo.indirectCharges) || 0;
                    addedDeduct += thisAmt + thisIndirect;
                }
            }

            if (addedDeduct > (availableTotalAmount - alreadyDeducted)) {
                alert(
                    `You don't have enough amount after deduction.\nAvailable: ${availableTotalAmount - alreadyDeducted}, required (New): ${addedDeduct}`
                );
                return;
            }
            // Standard structure submit
            let categories: any[] = [];
            Object.entries(categoryEntries).forEach(([category, entries]) => {
                entries.forEach((catData) => {
                    categories.push({
                        id: catData.id,
                        stock_name: catData.stockName,
                        unit: catData.unit,
                        total_amount: catData.totalAmount,
                        indirect_charges: catData.indirectCharges ?? null,
                        perUnitCharge: catData.perUnitCharge,
                        category: category,
                        investment_type: "structure", // *** ADDED investment_type for category entry ***
                        total_amount_category: catData.totalAmount // *** ADDED total_amount (as total_amount_category for category entry) ***
                    });
                });
            });
            let payload: any = {
                party_id: data.partyId,
                date: data.date ?? null,
                investment_type: 'structure',
                categories: categories
            };
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                const result = await res.json();
                if (!res.ok) throw new Error(result?.message || 'Failed to add stock');
                alert('Stock (Structure) added successfully!');
                setData({ partyId: '', date: '' });
                setCategoryEntries({});
                setDirectPaymentAmount('');
                setInvestmentType('structure');
                setDirectToStructureMeta(null);
                setHasDirectPending(false);
                globalEntryId = 1;
                // RELOAD PAGE after submit
                window.location.reload();
            } catch (err: any) {
                alert('Error: ' + (err?.message ?? 'Something went wrong'));
            }
            return;
        }
    };

    // Withdrawal logic (unchanged) --
    useEffect(() => {
        debugger;
        if (!withdrawal.partyId) {
            setWithdrawCategoriesDb([]);
            setWithdrawStocksDb([]);
            setWithdrawalInvestmentType('structure');
            setWithdrawalDirectPayment('');
            return;
        }
        const relevantDbRows: PartyStockDBType[] = (stocksDataDb || []).filter(row =>
            String(row.party_id) === String(withdrawal.partyId)
        );
        let newCats: string[] = [];
        let newStocksDb: StockDBCategoryItem[] = [];
        for (const row of relevantDbRows) {
            let catsArr: StockDBCategoryItem[] = [];
            if (typeof row.categories === 'string') {
                try {
                    const parsed = JSON.parse(row.categories);
                    // Only include items that look like StockDBCategoryItem (i.e., have required keys)
                    catsArr = Array.isArray(parsed) ? parsed.filter(item =>
                        typeof item === "object" &&
                        item !== null &&
                        "category" in item &&
                        "stock_name" in item &&
                        "unit" in item &&
                        "total_amount" in item &&
                        "indirect_charges" in item &&
                        "perUnitCharge" in item
                    ) : [];
                } catch (e) {
                    catsArr = [];
                }
            } else if (Array.isArray(row.categories)) {
                // Only include objects that match shape of StockDBCategoryItem
                catsArr = (row.categories as any[]).filter(item =>
                    typeof item === "object" &&
                    item !== null &&
                    "category" in item &&
                    "stock_name" in item &&
                    "unit" in item &&
                    "total_amount" in item &&
                    "indirect_charges" in item &&
                    "perUnitCharge" in item
                ) as StockDBCategoryItem[];
            }
            debugger;
            catsArr.forEach(item => {
                if (item.category && !newCats.includes(item.category)) {
                    newCats.push(item.category);
                }
                newStocksDb.push(item);
            });
        }
        setWithdrawCategoriesDb(newCats);
        setWithdrawStocksDb(newStocksDb);
        setWithdrawalInvestmentType('structure');
        setWithdrawalDirectPayment('');
    }, [withdrawal.partyId, stocksDb]);

    const withdrawalStockOptions = useMemo<StockDBCategoryItem[]>(() => {
        if (!withdrawal.partyId || !withdrawal.category) return [];
        return withdrawStocksDb.filter(item => item.category === withdrawal.category);
    }, [withdrawal.partyId, withdrawal.category, withdrawStocksDb]);

    const withdrawalUnitLimit: number = useMemo(() => {
        if (!withdrawal.partyId || !withdrawal.category || !withdrawal.stockName) return 0;
        const item = withdrawalStockOptions.find(
            s => s.stock_name === withdrawal.stockName
        );
        let unit = 0;
        if (item && item.unit) {
            const n = parseFloat(item.unit);
            if (!isNaN(n)) unit = n;
        }
        return unit;
    }, [withdrawal.partyId, withdrawal.category, withdrawal.stockName, withdrawalStockOptions]);

    const handleWithdrawalInput = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;

        if (name === 'withdrawalInvestmentType') {
            setWithdrawalInvestmentType(value as 'direct' | 'structure');
            setWithdrawalDirectPayment('');
            setWithdrawal((prev) => ({
                ...prev,
                category: '',
                stockId: '',
                stockName: '',
                sale: '',
                date: '',
            }));
            return;
        }

        setWithdrawal(prev => {
            let changes: any = { [name]: value };
            if (name === "partyId") {
                changes = { ...changes, category: '', stockId: '', stockName: '', sale: '', date: '' };
            }
            if (name === "category") {
                changes = { ...changes, stockId: '', stockName: '', sale: '' };
            }
            if (name === "stockName") {
                let foundStock = withdrawalStockOptions.find(item => item.stock_name === value);
                changes.stockId = foundStock ? foundStock.id : '';
            }
            return { ...prev, ...changes };
        });
    };

    const resetWithdrawalForm = () => {
        setWithdrawal({
            partyId: '',
            category: '',
            stockId: '',
            stockName: '',
            sale: '',
            date: '',
        });
        setWithdrawalInvestmentType('structure');
        setWithdrawalDirectPayment('');
    };

    const handleWithdrawalSubmit = async (e?: React.FormEvent) => {
        debugger;
        if (e) e.preventDefault();
       

        if (!withdrawal.partyId) {
            alert("Please select a party for withdrawal.");
            return;
        }

        if (withdrawalInvestmentType === 'direct') {
            if (!withdrawalDirectPayment || isNaN(Number(withdrawalDirectPayment)) || Number(withdrawalDirectPayment) <= 0) {
                alert('Please enter a valid payment amount');
                return;
            }
            const payload = {
                party_id: withdrawal.partyId,
                date: withdrawal.date || null,
                amount: withdrawalDirectPayment,
                categories: JSON.stringify([{ investment_type: 'direct' }])
            };
            setWithdrawalLoading(true);
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/auth/addwithdrawal`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                    }
                );
                const result = await res.json();
                if (!res.ok) {
                    throw new Error(result?.message || "Failed to add withdrawal");
                }
                alert("Withdrawal added successfully!");
                resetWithdrawalForm();
                setShowWithdrawalModal(false);
                // RELOAD PAGE after withdrawal submit
                window.location.reload();
            } catch (err: any) {
                alert("Error: " + (err?.message ?? "Something went wrong"));
            } finally {
                setWithdrawalLoading(false);
            }
            return;
        }

        if (!withdrawal.category) {
            alert("Please select a category.");
            return;
        }
        if (!withdrawal.stockName) {
            alert("Please select a stock.");
            return;
        }
        if (!withdrawal.stockId) {
            alert("Invalid or missing stock id.");
            return;
        }
       
        if (!withdrawal.date) {
            alert("Please select a date.");
            return;
        }
        const saleValue = parseFloat(withdrawal.sale);
        if (withdrawalUnitLimit > 0 && saleValue > withdrawalUnitLimit) {
            alert(
                `You don't have enough unit for this withdrawal. Available unit: ${withdrawalUnitLimit}, you entered: ${withdrawal.sale}`
            );
            return;
        }
        if (withdrawalUnitLimit === 0) {
            alert(
                `No units found for this stock under the selected party and category.`
            );
            return;
        }
        if (!withdrawal.stockId || !String(withdrawal.stockId).trim()) {
            alert("Stock ID cannot be blank.");
            return;
        }

        // Build categories as an array with all required fields and stringify
        const categoriesArr = [
            {
                category: withdrawal.category,
                stockName: withdrawal.stockName,
                investment_type: 'indirect'
            }
        ];

        const payload = {
            party_id: withdrawal.partyId,
            categories: JSON.stringify(categoriesArr),
            stock_id: withdrawal.stockId,
            amount: withdrawalDirectPayment,
            date: withdrawal.date,
            investment_type: 'structure',
        };

        setWithdrawalLoading(true);

        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/auth/addwithdrawal`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                }
            );
            const result = await res.json();
            if (!res.ok) {
                throw new Error(result?.message || "Failed to add withdrawal");
            }
            alert("Withdrawal added successfully!");
            resetWithdrawalForm();
            setShowWithdrawalModal(false);
            // RELOAD PAGE after withdrawal submit
            window.location.reload();
        } catch (err: any) {
            alert("Error: " + (err?.message ?? "Something went wrong"));
        } finally {
            setWithdrawalLoading(false);
        }
    };

    return (
        <>
            <div className='p-6'>
                <div className='w-full flex justify-between'>
                    <h1 className='text-[28px] text-[#101828] font-[500]'>Add new stock</h1>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            className="flex items-center justify-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-8"
                            onClick={() => setShowWithdrawalModal(true)}
                        >
                            Add Withdrawal
                        </button>
                        <button
                            type="button"
                            className="flex items-center justify-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#FF9500] to-[#1CAC70] py-2 rounded-[8px] px-8"
                            onClick={() => setShowSellStockModal(true)}
                        >
                            Sell Stock
                        </button>
                    </div>
                </div>
                <form
                    className='bg-[#ffff] p-5 w-full rounded-[10px] mt-4'
                    onSubmit={handleSubmit}
                    autoComplete="off"
                >
                    <div className='flex flex-wrap w-full justify-between mt-4 gap-4'>
                        {/* Party selection */}
                        <div className='w-[100%] md:w-[48%]'>
                            <label htmlFor="partyId">Party Name</label>
                            <br />
                            <select
                                name="partyId"
                                id="partyId"
                                value={data.partyId}
                                onChange={handleInput}
                                className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                required
                            >
                                <option value="">Choose Party</option>
                                {partiesList.map((p) => (
                                    <option key={p.id} value={String(p.id)}>
                                        {p.name}
                                    </option>
                                ))}
                            </select>
                            {selectedPartyTotalAmount !== null && (
                                <div className="text-xs text-blue-600 mt-1">
                                    Total Available Amount: {selectedPartyTotalAmount}
                                </div>
                            )}
                            {deductedAvailable !== null && (
                                <div className="text-xs mt-1" style={{ color: deductedAvailable < 0 ? "#D8000C" : "#007200" }}>
                                    Available After Deduction: {deductedAvailable}
                                </div>
                            )}
                        </div>
                        {/* Date of Purchase */}
                        <div className='w-[100%] md:w-[48%]'>
                            <label htmlFor="date">Date Of Purchase</label>
                            <br />
                            <input
                                type="date"
                                name="date"
                                id="date"
                                placeholder='Purchase date'
                                value={data.date}
                                onChange={handleInput}
                                className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                autoComplete="off"
                            />
                        </div>
                        {/* Investment Type */}
                        <div className='flex flex-wrap w-full justify-between mt-4 gap-4'>
                        <div className='w-[100%] md:w-[48%]'>
                            <label htmlFor="investmentType">Investment</label>
                            <br />
                            <select
                                name="investmentType"
                                id="investmentType"
                                value={investmentType}
                                onChange={handleInvestmentTypeChange}
                                className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                required
                            >
                                <option value="direct">Direct Payment</option>
                                <option value="structure">Structure Payment</option>
                            </select>
                        </div>
                    </div>
                    {investmentType === 'direct' && (
                        <div className='flex flex-wrap w-full justify-between mt-4 gap-4'>
                            <div className='w-[100%] md:w-[48%]'>
                                <label htmlFor="directPaymentAmount">Payment Amount</label>
                                <br />
                                <input
                                    type="number"
                                    name="directPaymentAmount"
                                    id="directPaymentAmount"
                                    placeholder="Enter payment amount"
                                    value={directPaymentAmount}
                                    min={0}
                                    onChange={e => setDirectPaymentAmount(e.target.value)}
                                    className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                    required
                                    max={selectedPartyTotalAmount != null && stocksDataDb.length > 0 ?
                                        Math.max(selectedPartyTotalAmount - calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId), 0) :
                                        undefined}
                                />
                                {selectedPartyTotalAmount !== null && !!directPaymentAmount && (
                                    <div className="text-xs mt-1"
                                        style={{
                                            color: Number(directPaymentAmount) > (selectedPartyTotalAmount - calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId)) ? "#D8000C" : "#007200"
                                        }}>
                                        {Number(directPaymentAmount) > (selectedPartyTotalAmount - calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId))
                                            ? `Not enough available. Max: ${selectedPartyTotalAmount - calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId)}`
                                            : `You have enough amount.`}
                                    </div>
                                )}
                                <p className='mt-5 w-full'>Categories</p>
                            <div className="flex flex-wrap gap-4 p-3 bg-gradient-to-r from-[#F9FAFB] to-[#F4F2FF] rounded-xl shadow-sm border border-[#ececec] w-full">
                                {possibleCategories.map((cat) => (
                                    <div
                                        key={cat.id}
                                        className={`flex items-center gap-2 py-2 px-4 rounded-lg transition-all duration-200 cursor-pointer border-2 ${
                                            isChecked(cat)
                                                ? "bg-gradient-to-r from-[#1CAC70]/10 to-[#FF9500]/10 border-[#FF9500] shadow-md text-[#FF9500] font-semibold"
                                                : "bg-white border-[#ececec] hover:shadow-lg text-[#344054]"
                                        }`}
                                        style={{ minWidth: 180 }}
                                        onClick={() => handleCategory(cat)}
                                        tabIndex={0}
                                        role="button"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked(cat)}
                                            onChange={() => handleCategory(cat)}
                                            className="form-checkbox h-5 w-5 accent-[#FF9500] rounded focus:ring-2 focus:ring-[#FF9500] transition"
                                            style={{ cursor: "pointer" }}
                                            onClick={e => e.stopPropagation()}
                                        />
                                        <span className="text-[16px]">{cat.category_name}</span>
                                    </div>
                                ))}
                            </div>
                                {deductedAvailable !== null && (
                                    <div className="text-xs mt-1" style={{ color: deductedAvailable < 0 ? "#D8000C" : "#007200" }}>
                                        Available After Deduction: {deductedAvailable}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {investmentType === 'structure' && (
                        <>
                            <p className='mt-5 w-full'>Categories</p>
                            <div className="flex flex-col gap-2 p-2">
                                {possibleCategories.map((cat) => {
                                    const entries = categoryEntries[cat.category_name] || [];
                                    return (
                                        <div key={cat.id} className="flex flex-col gap-1">
                                            <label className="flex items-center gap-2 cursor-pointer text-[#344054] text-[16px]">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked(cat)}
                                                    onChange={() => handleCategory(cat)}
                                                />
                                                {cat.category_name}
                                            </label>
                                            {isChecked(cat) && (
                                                <div className="flex flex-col gap-3 ml-5 w-full border-l-[2px] border-[#ececec] pl-4 mt-2 mb-2">
                                                    {(entries || []).map((entry, entryIdx) => {
                                                        let currErr = entryErrors?.[cat.category_name]?.[entryIdx];
                                                        // Calculate allowed max for input
                                                        const alreadyDeducted = calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId);
                                                        let previousSum = 0;
                                                        Object.entries(categoryEntries).forEach(([catKey, ents]) => {
                                                            ents.forEach((e, i) => {
                                                                if (
                                                                    catKey === cat.category_name && i >= entryIdx
                                                                ) return;
                                                                let a = Number(e.totalAmount) || 0;
                                                                let b = Number(e.indirectCharges) || 0;
                                                                previousSum += a + b;
                                                            });
                                                        });
                                                        const maxAllowed =
                                                            selectedPartyTotalAmount != null
                                                                ? Math.max(selectedPartyTotalAmount - alreadyDeducted - previousSum, 0)
                                                                : undefined;
                                                        return (
                                                            <div key={`${cat.category_name}-entry-${entry.id}`} className="flex flex-wrap gap-2 items-end w-full relative">
                                                                <div className='flex flex-col'>
                                                                    <label htmlFor={`stockid-${cat.category_name}-${entryIdx}`} className="text-[13px]">ID</label>
                                                                    <input
                                                                        id={`stockid-${cat.category_name}-${entryIdx}`}
                                                                        type="text"
                                                                        value={entry.id}
                                                                        readOnly
                                                                        className="w-[60px] bg-gray-50 border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#5E5E5E] text-[13px] outline-none"
                                                                    />
                                                                </div>
                                                                <div className='flex flex-col'>
                                                                    <label htmlFor={`stockname-${cat.category_name}-${entryIdx}`} className="text-[13px]">Stock Name</label>
                                                                    <input
                                                                        id={`stockname-${cat.category_name}-${entryIdx}`}
                                                                        type="text"
                                                                        placeholder="Enter Stock Name"
                                                                        value={entry.stockName || ''}
                                                                        onChange={e =>
                                                                            handleCategoryFieldChange(cat, entryIdx, "stockName", e.target.value)
                                                                        }
                                                                        required
                                                                        className="w-[140px] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[13px] outline-none"
                                                                    />
                                                                </div>
                                                                <div className='flex flex-col'>
                                                                    <label htmlFor={`unit-${cat.category_name}-${entryIdx}`} className="text-[13px]">Unit</label>
                                                                    <input
                                                                        id={`unit-${cat.category_name}-${entryIdx}`}
                                                                        type="number"
                                                                        min={0}
                                                                        step="any"
                                                                        placeholder="Enter unit"
                                                                        value={entry.unit}
                                                                        onChange={e =>
                                                                            handleCategoryFieldChange(cat, entryIdx, "unit", e.target.value)
                                                                        }
                                                                        className="w-[120px] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[13px] outline-none"
                                                                        required
                                                                    />
                                                                </div>
                                                                <div className='flex flex-col'>
                                                                    <label htmlFor={`ta-${cat.category_name}-${entryIdx}`} className="text-[13px]">
                                                                        Total Amount
                                                                    </label>
                                                                    <input
                                                                        id={`ta-${cat.category_name}-${entryIdx}`}
                                                                        type="number"
                                                                        min={0}
                                                                        step="any"
                                                                        max={maxAllowed !== undefined && entry.indirectCharges
                                                                            ? Math.max(maxAllowed - (Number(entry.indirectCharges) || 0), 0)
                                                                            : maxAllowed}
                                                                        style={
                                                                            currErr
                                                                                ? { borderColor: '#D8000C', background: '#fff7f7' }
                                                                                : undefined
                                                                        }
                                                                        placeholder="Total amount for this stock"
                                                                        value={entry.totalAmount}
                                                                        onChange={e => {
                                                                            // Don't allow input > maxAllowed (after indirectCharges). If it's too much, set error.
                                                                            let inputValue = e.target.value;
                                                                            let indirectThis = Number(entry.indirectCharges) || 0;
                                                                            let maxForTA = maxAllowed !== undefined ? Math.max(maxAllowed - indirectThis, 0) : undefined;
                                                                            if (
                                                                                maxForTA != null &&
                                                                                inputValue !== '' &&
                                                                                Number(inputValue) > maxForTA
                                                                            ) {
                                                                                inputValue = String(maxForTA);
                                                                            }
                                                                            handleCategoryFieldChange(cat, entryIdx, "totalAmount", inputValue);
                                                                        }}
                                                                        className={
                                                                            "w-[140px] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[13px] outline-none"
                                                                            + (currErr ? " border-red-500 bg-[#fff7f7]"
                                                                                : "")
                                                                        }
                                                                        required
                                                                        disabled={maxAllowed !== undefined && maxAllowed <= 0}
                                                                    />
                                                                </div>
                                                                <div className='flex flex-col'>
                                                                    <label htmlFor={`ic-${cat.category_name}-${entryIdx}`} className="text-[13px]">Indirect Charges</label>
                                                                    <input
                                                                        id={`ic-${cat.category_name}-${entryIdx}`}
                                                                        type="number"
                                                                        min={0}
                                                                        step="any"
                                                                        max={maxAllowed !== undefined && entry.totalAmount
                                                                            ? Math.max(maxAllowed - (Number(entry.totalAmount) || 0), 0)
                                                                            : maxAllowed}
                                                                        placeholder="Indirect charges"
                                                                        value={entry.indirectCharges}
                                                                        onChange={e => {
                                                                            // Don't allow input > maxAllowed (after totalAmount). If it's too much, set error.
                                                                            let inputValue = e.target.value;
                                                                            let totalThis = Number(entry.totalAmount) || 0;
                                                                            let maxForIC = maxAllowed !== undefined ? Math.max(maxAllowed - totalThis, 0) : undefined;
                                                                            if (
                                                                                maxForIC != null &&
                                                                                inputValue !== '' &&
                                                                                Number(inputValue) > maxForIC
                                                                            ) {
                                                                                inputValue = String(maxForIC);
                                                                            }
                                                                            handleCategoryFieldChange(cat, entryIdx, "indirectCharges", inputValue);
                                                                        }}
                                                                        className={
                                                                            "w-[140px] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[13px] outline-none"
                                                                            + (currErr ? " border-red-500 bg-[#fff7f7]"
                                                                                : "")
                                                                        }
                                                                    />
                                                                </div>
                                                                <div className='flex flex-col'>
                                                                    <label htmlFor={`puc-${cat.category_name}-${entryIdx}`} className="text-[13px]">Per Unit Charge</label>
                                                                    <input
                                                                        id={`puc-${cat.category_name}-${entryIdx}`}
                                                                        type="number"
                                                                        min={0}
                                                                        step="any"
                                                                        placeholder="Per Unit Charge"
                                                                        value={entry.perUnitCharge}
                                                                        onChange={e =>
                                                                            handleCategoryFieldChange(cat, entryIdx, "perUnitCharge", e.target.value)
                                                                        }
                                                                        className={`w-[140px] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[13px] outline-none ${entry.autoCalculated && !!entry.unit ? "bg-gray-100" : ""}`}
                                                                        readOnly={entry.autoCalculated && !!entry.unit}
                                                                        required
                                                                    />
                                                                    {entry.autoCalculated && !!entry.unit && (
                                                                        <span className="text-xs text-green-500">Auto-calculated</span>
                                                                    )}
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    title="Remove entry"
                                                                    onClick={() => handleRemoveEntry(cat, entryIdx)}
                                                                    className="ml-2 text-red-500 hover:text-red-700 rounded px-2 py-1 border border-red-200 bg-red-50 flex items-center justify-center text-xs"
                                                                    style={{ height: '32px' }}
                                                                >
                                                                    Remove
                                                                </button>
                                                                {currErr && (
                                                                    <div className="text-xs text-red-600 w-full">{currErr}</div>
                                                                )}
                                                            </div>
                                                        )
                                                    })}
                                                    <button
                                                        type="button"
                                                        className="mt-1 text-sm text-blue-500 hover:text-blue-700 font-semibold px-2 py-1 border border-blue-200 rounded bg-blue-50 flex items-center w-fit mb-1"
                                                        onClick={() => handleAddEntry(cat)}
                                                        disabled={
                                                            (() => {
                                                                // Prevent adding if available amount is exhausted
                                                                const sumEntries = Object.values(categoryEntries).flat().reduce((acc, e) => acc + (Number(e.totalAmount) || 0) + (Number(e.indirectCharges) || 0), 0);
                                                                const alreadyDeducted = calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId);
                                                                return selectedPartyTotalAmount != null && alreadyDeducted + sumEntries >= selectedPartyTotalAmount;
                                                            })()
                                                        }
                                                    >
                                                        + Add another for {cat.category_name}
                                                    </button>
                                                    {selectedPartyTotalAmount !== null && entries.length > 0 && (
                                                        <div className="mt-1 text-xs text-blue-700">
                                                            {(() => {
                                                                // Show user how much has been used/allocated in DB (from stockdata), new allocation, and after deduction
                                                                const alreadyDeducted = calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId);
                                                                let additional = 0;
                                                                Object.entries(categoryEntries).forEach(([category, ents]) => {
                                                                    ents.forEach(ent => {
                                                                        let amt = parseFloat(ent.totalAmount || "0");
                                                                        let indirect = parseFloat(ent.indirectCharges || "0");
                                                                        if (!isNaN(amt)) additional += amt;
                                                                        if (!isNaN(indirect)) additional += indirect;
                                                                    });
                                                                });
                                                                return `Total used (from stock table): ${alreadyDeducted}, New allocation: ${additional}, of available: ${selectedPartyTotalAmount}`;
                                                            })()}
                                                            {(() => {
                                                                const alreadyDeducted = calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId);
                                                                let additional = 0;
                                                                Object.entries(categoryEntries).forEach(([category, ents]) => {
                                                                    ents.forEach(ent => {
                                                                        let amt = parseFloat(ent.totalAmount || "0");
                                                                        let indirect = parseFloat(ent.indirectCharges || "0");
                                                                        if (!isNaN(amt)) additional += amt;
                                                                        if (!isNaN(indirect)) additional += indirect;
                                                                    });
                                                                });
                                                                if (alreadyDeducted + additional > selectedPartyTotalAmount)
                                                                    return (
                                                                        <span className="text-red-500 ml-3">
                                                                            Not enough amount!
                                                                        </span>
                                                                    );
                                                                return null;
                                                            })()}
                                                            {deductedAvailable !== null && (
                                                                <span className="ml-4" style={{ color: deductedAvailable < 0 ? "#D8000C" : "#007200" }}>
                                                                    Available After Deduction: {deductedAvailable}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                    </div>
                    <button
                        type="submit"
                        className="mt-6 flex items-center justify-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-8"
                    >
                        Submit
                    </button>
                </form>
            </div>
            {/* ============== Sell Stock Modal ============== */}
            {showSellStockModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md h-full max-h-[90vh] flex flex-col">
                        {/* Header */}
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
                            <h2 className="text-xl font-semibold text-gray-800">Sell Stock</h2>
                            <button
                                onClick={() => {
                                    setShowSellStockModal(false);
                                    setSellStockForm({
                                        partyId: '',
                                        category: '',
                                        stockId: '',
                                        stockName: '',
                                        sellUnits: '',
                                        sellAmount: '',
                                        indirectCharges: '',
                                        date: '',
                                    });
                                }}
                                className="text-gray-400 hover:text-gray-700 text-2xl"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        {/* Scrollable Form */}
                        <div className="overflow-y-auto flex-1 px-6 py-4">
                            <form
                                id="sell-stock-form-modal"
                                onSubmit={handleSellStockSubmit}
                                autoComplete="off"
                                className="flex flex-col gap-4"
                            >
                                <div>
                                    <label htmlFor="sellstock-partyId" className="block text-sm font-medium text-gray-700 mb-1">
                                        Party Name
                                    </label>
                                    <select
                                        name="partyId"
                                        id="sellstock-partyId"
                                        value={sellStockForm.partyId}
                                        onChange={handleSellStockFormChange}
                                        required
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="">Choose Party</option>
                                        {partiesList.map((p) => (
                                            <option key={p.id} value={String(p.id)}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="sellstock-category" className="block text-sm font-medium text-gray-700 mb-1">
                                        Category
                                    </label>
                                    <select
                                        name="category"
                                        id="sellstock-category"
                                        value={sellStockForm.category}
                                        onChange={handleSellStockFormChange}
                                        required
                                        disabled={!sellStockForm.partyId}
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                                    >
                                        <option value="">Select Category</option>
                                        {sellPartyCategories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="sellstock-stockId" className="block text-sm font-medium text-gray-700 mb-1">
                                        Stock Name
                                    </label>
                                    <select
                                        name="stockId"
                                        id="sellstock-stockId"
                                        value={sellStockForm.stockId}
                                        onChange={handleSellStockFormChange}
                                        required
                                        disabled={!sellStockForm.partyId || !sellStockForm.category}
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                                    >
                                        <option value="">Select Stock</option>
                                        {sellCategoryStocks.map(stock => (
                                            <option key={stock.id} value={stock.id}>
                                                {stock.stock_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="sellstock-sellUnits" className="block text-sm font-medium text-gray-700 mb-1">
                                        Units To Sell
                                        {sellStockForm.partyId && sellStockForm.category && sellStockForm.stockId ? (
                                            <span className="text-xs text-gray-500 ml-2">
                                                (Available: {sellStockAvailableDetails.unit})
                                            </span>
                                        ) : null}
                                    </label>
                                    <input
                                        type="number"
                                        name="sellUnits"
                                        id="sellstock-sellUnits"
                                        placeholder="How many units to sell"
                                        value={sellStockForm.sellUnits}
                                        min={0}
                                        max={sellStockAvailableDetails.unit || undefined}
                                        onChange={handleSellStockFormChange}
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                                        required
                                        disabled={!sellStockForm.stockId}
                                    />
                                    {sellStockForm.sellUnits && parseFloat(sellStockForm.sellUnits) > sellStockAvailableDetails.unit ? (
                                        <span className="text-xs text-red-500">
                                            Not enough units available!
                                        </span>
                                    ) : null}
                                </div>
                                <div>
                                    <label htmlFor="sellstock-sellAmount" className="block text-sm font-medium text-gray-700 mb-1">
                                        Amount for which you are selling stocks
                                    </label>
                                    <input
                                        type="number"
                                        name="sellAmount"
                                        id="sellstock-sellAmount"
                                        placeholder="Enter sell amount"
                                        value={sellStockForm.sellAmount || ""}
                                        min={0}
                                        step="any"
                                        onChange={handleSellStockFormChange}
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                                        required
                                        disabled={!sellStockForm.stockId}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="sellstock-indirectCharges" className="block text-sm font-medium text-gray-700 mb-1">
                                        Indirect Charges
                                    </label>
                                    <input
                                        type="number"
                                        name="indirectCharges"
                                        id="sellstock-indirectCharges"
                                        placeholder="Enter indirect charges (if any)"
                                        value={sellStockForm.indirectCharges || ""}
                                        min={0}
                                        step="any"
                                        onChange={handleSellStockFormChange}
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                                        disabled={!sellStockForm.stockId}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="sellstock-date" className="block text-sm font-medium text-gray-700 mb-1">
                                        Date
                                    </label>
                                    <input
                                        type="date"
                                        name="date"
                                        id="sellstock-date"
                                        value={sellStockForm.date}
                                        onChange={handleSellStockFormChange}
                                        required
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                                        disabled={!sellStockForm.stockId}
                                    />
                                </div>
                                <div className="bg-gray-50 rounded-md p-3 text-sm">
                                    <label className="block font-medium text-gray-700 mb-1">
                                        Sell Amount (auto):
                                        <span className="font-semibold text-gray-800 ml-2">
                                            {sellStockForm.sellAmount && sellStockForm.sellUnits && parseFloat(sellStockForm.sellUnits) > 0
                                                ? (
                                                    <>
                                                        {(parseFloat(sellStockForm.sellAmount) / parseFloat(sellStockForm.sellUnits)).toFixed(2)}
                                                        <span className="text-xs text-gray-500 ml-2">(per unit)</span>
                                                    </>
                                                )
                                                : (
                                                    <>
                                                        {sellStockFormAmount.toFixed(2)}
                                                    </>
                                                )
                                            }
                                        </span>
                                    </label>
                                    <div className="text-xs text-gray-500">
                                        {sellStockForm.sellAmount && sellStockForm.sellUnits && parseFloat(sellStockForm.sellUnits) > 0
                                            ? "Calculated as: selling amount ÷ units to sell"
                                            : "Calculated as: (current_amount of stock ÷ total units) × units to sell"}
                                    </div>
                                    {sellStockForm.indirectCharges && parseFloat(sellStockForm.indirectCharges) > 0 && (
                                        <div className="text-xs text-green-600 mt-1">
                                            Indirect charges will also be recorded for this sale.
                                        </div>
                                    )}
                                </div>
                            </form>
                        </div>
                        {/* Footer buttons, always visible */}
                        <div className="flex gap-3 px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSellStockModal(false);
                                    setSellStockForm({
                                        partyId: '',
                                        category: '',
                                        stockId: '',
                                        stockName: '',
                                        sellUnits: '',
                                        sellAmount: '',
                                        indirectCharges: '',
                                        date: '',
                                    });
                                }}
                                className="flex-1 bg-white border border-gray-300 text-gray-800 rounded-md py-2 font-medium hover:bg-gray-100 transition"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="sell-stock-form-modal"
                                disabled={sellStockLoading}
                                className="flex-1 bg-primary-600 text-white rounded-md py-2 font-medium hover:bg-primary-700 transition disabled:opacity-60"
                                style={{ background: "#1CAC70" }}
                            >
                                {sellStockLoading ? "Saving..." : "Sell Stock"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* ============ Withdrawal Modal & Form =========== */}
            {showWithdrawalModal && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40">
                    <div className='bg-white rounded-2xl shadow-xl p-6 w-[90vw] max-w-[400px] flex flex-col gap-4'>
                        <div className="flex justify-between items-center mb-2">
                            <h2 className="text-lg font-semibold text-gray-800">Add Withdrawal</h2>
                            <button
                                onClick={() => {
                                    setShowWithdrawalModal(false);
                                    resetWithdrawalForm();
                                }}
                                className="text-gray-400 hover:text-gray-700 text-xl font-bold"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleWithdrawalSubmit} autoComplete="off">
                            <div className='mb-2'>
                                <label htmlFor="withdrawal-partyId">Party Name</label>
                                <select
                                    name="partyId"
                                    id="withdrawal-partyId"
                                    value={withdrawal.partyId}
                                    onChange={handleWithdrawalInput}
                                    required
                                    className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                >
                                    <option value="">Choose Party</option>
                                    {partiesList.map((p) => (
                                        <option key={p.id} value={String(p.id)}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className='mb-2'>
                                <label htmlFor="withdrawal-investmentType">Investment</label>
                                <select
                                    name="withdrawalInvestmentType"
                                    id="withdrawal-investmentType"
                                    value={withdrawalInvestmentType}
                                    onChange={handleWithdrawalInput}
                                    required
                                    className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                    disabled={!withdrawal.partyId}
                                >
                                    <option value="direct">Direct Payment</option>
                                    <option value="structure">Structure Payment</option>
                                </select>
                            </div>
                            {withdrawalInvestmentType === 'direct' && (
                                <div className='mb-2'>
                                    <label htmlFor="withdrawal-directPayment">Payment Amount</label>
                                    <input
                                        type="number"
                                        name="withdrawalDirectPayment"
                                        id="withdrawal-directPayment"
                                        placeholder="Enter payment amount"
                                        value={withdrawalDirectPayment}
                                        min={0}
                                        onChange={e => setWithdrawalDirectPayment(e.target.value)}
                                        className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                        required
                                        disabled={!withdrawal.partyId}
                                    />
                                    <div className="mt-2">
                                        <label htmlFor="withdrawal-date-direct">Date</label>
                                        <input
                                            type="date"
                                            name="date"
                                            id="withdrawal-date-direct"
                                            value={withdrawal.date}
                                            onChange={handleWithdrawalInput}
                                            required
                                            className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                            disabled={!withdrawal.partyId}
                                        />
                                    </div>
                                </div>
                            )}
                            {withdrawalInvestmentType === 'structure' && (
                                <>
                                    <div className='mb-2'>
                                        <label htmlFor="withdrawal-category">Category</label>
                                        <select
                                            name="category"
                                            id="withdrawal-category"
                                            value={withdrawal.category}
                                            onChange={handleWithdrawalInput}
                                            required
                                            className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                            disabled={!withdrawal.partyId}
                                        >
                                            <option value="">Choose Category</option>
                                            {withdrawCategoriesDb.map((cat) => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className='mb-2'>
                                        <label htmlFor="withdrawal-stockName">Stock Name</label>
                                        <select
                                            name="stockName"
                                            id="withdrawal-stockName"
                                            value={withdrawal.stockName || ''}
                                            onChange={handleWithdrawalInput}
                                            required
                                            className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                            disabled={!withdrawal.category}
                                        >
                                            <option value="">Select Stock</option>
                                            {withdrawalStockOptions.map((item) => (
                                                <option key={item.id} value={item.stock_name}>
                                                    {item.stock_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className='mb-2'>
                                        <label htmlFor="withdrawal-amount">
                                            Withdrawal Amount
                                            {withdrawal.partyId && withdrawal.category && withdrawal.stockName && (
                                                <span className="text-xs text-blue-500 ml-2">
                                                    (
                                                    Available unit: {withdrawalUnitLimit}
                                                    )
                                                </span>
                                            )}
                                        </label>
                                        <input
                                            type="number"
                                            name="withdrawalAmount"
                                            id="withdrawal-amount"
                                            placeholder="Withdrawal Amount"
                                            value={withdrawalDirectPayment}
                                        min={0}
                                        onChange={e => setWithdrawalDirectPayment(e.target.value)}
                                            required
                                            className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                            disabled={!withdrawal.stockName}
                                        />
                                        
                                    </div>
                                    <div className='mb-2'>
                                        <label htmlFor="withdrawal-date">Date</label>
                                        <input
                                            type="date"
                                            name="date"
                                            id="withdrawal-date"
                                            value={withdrawal.date}
                                            onChange={handleWithdrawalInput}
                                            required
                                            className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                            disabled={!withdrawal.stockName}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="flex gap-2 mt-6">
                                <button
                                    type="submit"
                                    disabled={withdrawalLoading}
                                    className="bg-gradient-to-r from-[#1CAC70] to-[#FF9500] text-white px-6 py-2 rounded-lg font-semibold flex-1 disabled:opacity-60"
                                >
                                    {withdrawalLoading ? "Saving..." : "Submit Withdrawal"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowWithdrawalModal(false);
                                        resetWithdrawalForm();
                                    }}
                                    className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-semibold flex-1"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}

export default Addstock;
